import { produce } from "immer";
import {
  MutableRefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Flex, Text, ScrollArea, Container, TextField } from "@radix-ui/themes";
import { z } from "zod";
import { toast } from "sonner";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

import { Tabs } from "@/v2/components/tabs/Tabs";
import Switch from "@/components/shared/inputs/switch";
import { MultiSelectDropdown } from "./MultiSelectDropdown";
import { useExploreValues } from "@/v2/entities/explore/Explore.api";
import { useIntegrations } from "@/api/integration/Integration.api";
import { isIntegrationCompleted } from "@/api/integration/Integration.model";

import styles from "./Drawer.module.scss";
import { KnowledgeHubConfig } from "../core/pages/knowledge-hub/index.types";
import { OIDCConfigurationFields, OIDCField } from "./OIDCConfigurationFields";

const authEnabledSchema = z.object({
  oidcClientId: z.string().min(1, "OIDC Client ID is required"),
  oidcAuthority: z.string().min(1, "OIDC Authority is required"),
});

export const ConfigurationDrawer = ({
  config,
  updateConfiguration,
  publishActionRef,
}: {
  config: KnowledgeHubConfig;
  updateConfiguration: (configuration: KnowledgeHubConfig) => void;
  publishActionRef: MutableRefObject<(() => boolean | void) | null>;
}) => {
  const [localConfig, setLocalConfig] = useState<KnowledgeHubConfig>(config);
  const localConfigRef = useRef(localConfig);

  const { data: exploreValues } = useExploreValues({ source: "tickets" });
  const { data: integrations } = useIntegrations();

  const statusOptions = useMemo(() => {
    return (
      exploreValues?.status?.map((s) => ({
        label: s,
        value: s,
      })) || []
    );
  }, [exploreValues]);

  const isSalesforceTicketSystemConnected = useMemo(
    () =>
      integrations?.some(
        (integration) =>
          integration.name === "salesforce" &&
          integration.type === "TICKET_SYSTEM" &&
          isIntegrationCompleted(integration)
      ) || false,
    [integrations]
  );

  const [lastCustomTicketName, setLastCustomTicketName] = useState(() => {
    const val = config.auth?.supportTickets?.endUserTicketName;
    if (val && !["Tickets", "Cases"].includes(val)) {
      return val;
    }
    return "Requests";
  });

  const handleSave = useCallback((): boolean => {
    const currentConfig = localConfigRef.current;

    if (currentConfig.auth?.enabled) {
      const result = authEnabledSchema.safeParse({
        oidcClientId: currentConfig.auth.oidcClientId,
        oidcAuthority: currentConfig.auth.oidcAuthority,
      });

      if (!result.success) {
        const firstError = result.error.errors[0];
        toast.error(firstError.message);
        return false;
      }
    }

    updateConfiguration(currentConfig);
    return true;
  }, [updateConfiguration]);

  useLayoutEffect(() => {
    publishActionRef.current = handleSave;

    return () => {
      publishActionRef.current = null;
    };
  }, [handleSave, publishActionRef]);

  useEffect(() => {
    localConfigRef.current = localConfig;
  }, [localConfig]);

  useEffect(() => {
    setLocalConfig(config);
    localConfigRef.current = config;

    const val = config.auth?.supportTickets?.endUserTicketName;
    if (val && !["Tickets", "Cases"].includes(val)) {
      setLastCustomTicketName(val);
    }
  }, [config]);

  const endUserTicketName = useMemo(
    () => localConfig.auth?.supportTickets?.endUserTicketName || "",
    [localConfig.auth?.supportTickets?.endUserTicketName]
  );

  const currentTab = useMemo(
    () =>
      ["Tickets", "Cases"].includes(endUserTicketName)
        ? endUserTicketName
        : "Custom",
    [endUserTicketName]
  );

  return (
    <ScrollArea>
      <div className={styles.drawerContainer}>
        <Flex gap="1" align="center" justify="between">
          <Text
            size="8"
            weight="medium"
            style={{ fontFamily: "Instrument Serif" }}
          >
            Configurations
          </Text>
        </Flex>

        <div className={styles.section}>
          <Flex direction="column" gap="1">
            <Flex justify="between" align="center">
              <Text size="2" weight="medium">
                Authentication
              </Text>
              <Switch
                value={localConfig.auth?.enabled || false}
                onChange={(checked) => {
                  setLocalConfig(
                    produce((draft) => {
                      if (!draft.auth) {
                        draft.auth = { enabled: false };
                      }
                      draft.auth.enabled = checked;
                    })
                  );
                }}
              />
            </Flex>
            <Text size="1" color="gray" style={{ maxWidth: "80%" }}>
              Enables user authentication via an external identity provider. This is required for authenticated features like Support Tickets.
            </Text>

            {localConfig.auth?.enabled && (
              <Accordion
                type="multiple"
                defaultValue={["oidc"]}
              >
                <AccordionItem value="oidc">
                  <AccordionTrigger>Identity Provider</AccordionTrigger>
                  <AccordionContent>
                    <div className={styles.section}>
                      <OIDCConfigurationFields
                        oidcClientId={localConfig.auth.oidcClientId}
                        oidcAuthority={localConfig.auth.oidcAuthority}
                        oidcAudience={localConfig.auth.oidcAudience}
                        onChange={(field: OIDCField, value: string) => {
                          setLocalConfig(
                            produce((draft) => {
                              if (draft.auth) {
                                draft.auth[field] = value;
                              }
                            })
                          );
                        }}
                      />
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="tickets">
                  <AccordionTrigger>Support Tickets</AccordionTrigger>
                  <AccordionContent>
                    <div className={styles.section}>
                      <Flex direction="column" gap="1">
                        <Flex justify="between" align="center">
                          <Text size="2" weight="medium">
                            Show Support Tickets
                          </Text>
                          <Switch
                            value={localConfig.auth.supportTickets?.enabled || false}
                            onChange={(checked) => {
                              setLocalConfig(
                                produce((draft) => {
                                  if (draft.auth) {
                                    if (!draft.auth.supportTickets) {
                                      draft.auth.supportTickets = { enabled: false };
                                    }
                                    draft.auth.supportTickets.enabled = checked;
                                  }
                                })
                              );
                            }}
                          />
                        </Flex>
                        <Text size="1" color="gray" style={{ maxWidth: "80%" }}>
                          Surface support tickets, including an open tickets indicator
                          on main page and a tickets page.
                        </Text>
                      </Flex>

                      {localConfig.auth.supportTickets?.enabled && (
                        <Flex direction="column" gap="4" mt="3">
                          {isSalesforceTicketSystemConnected && (
                            <Flex direction="column" gap="1">
                              <Flex justify="between" align="center">
                                <Text size="2" weight="medium">
                                  Allow user replies
                                </Text>
                                <Switch
                                  value={
                                    localConfig.auth.supportTickets
                                      ?.allowUserReplyToTicket || false
                                  }
                                  onChange={(checked) => {
                                    setLocalConfig(
                                      produce((draft) => {
                                        if (draft.auth) {
                                          if (!draft.auth.supportTickets) {
                                            draft.auth.supportTickets = {
                                              enabled: false,
                                            };
                                          }
                                          draft.auth.supportTickets.allowUserReplyToTicket =
                                            checked;
                                        }
                                      })
                                    );
                                  }}
                                />
                              </Flex>
                              <Text size="1" color="gray" style={{ maxWidth: "80%" }}>
                                Let signed-in users reply to their tickets from the
                                Knowledge Hub.
                              </Text>
                            </Flex>
                          )}

                          <Flex direction="column" gap="1">
                            <Text className={styles.label}>
                              Statuses to count as open (indicator)
                            </Text>
                            <MultiSelectDropdown
                              value={
                                localConfig.auth.supportTickets.statusesToCountAsOpen ||
                                []
                              }
                              onChange={(val) =>
                                setLocalConfig(
                                  produce((draft) => {
                                    if (draft.auth?.supportTickets) {
                                      draft.auth.supportTickets.statusesToCountAsOpen =
                                        val;
                                    }
                                  })
                                )
                              }
                              options={statusOptions}
                              placeholder="Select statuses..."
                            />
                          </Flex>

                          <Flex direction="column" gap="1">
                            <Text className={styles.label}>
                              Statuses to display on the page
                            </Text>
                            <MultiSelectDropdown
                              value={
                                localConfig.auth.supportTickets
                                  .statusesToDisplayOnThePage || []
                              }
                              onChange={(val) =>
                                setLocalConfig(
                                  produce((draft) => {
                                    if (draft.auth?.supportTickets) {
                                      draft.auth.supportTickets.statusesToDisplayOnThePage =
                                        val;
                                    }
                                  })
                                )
                              }
                              options={statusOptions}
                              placeholder="Select statuses..."
                            />
                          </Flex>

                          <Flex direction="column" gap="2">
                            <Text className={styles.label}>End User Ticket Name</Text>
                            <Tabs
                              fullWidth
                              variant="data"
                              value={currentTab}
                              onValueChange={(val) => {
                                setLocalConfig(
                                  produce((draft) => {
                                    if (draft.auth?.supportTickets) {
                                      if (val === "Custom") {
                                        draft.auth.supportTickets.endUserTicketName =
                                          lastCustomTicketName;
                                      } else {
                                        draft.auth.supportTickets.endUserTicketName =
                                          val;
                                      }
                                    }
                                  })
                                );
                              }}
                              tabs={[
                                { value: "Tickets", label: "Tickets" },
                                { value: "Cases", label: "Cases" },
                                { value: "Custom", label: "Custom" },
                              ]}
                            />
                            {currentTab === "Custom" && (
                              <TextField.Root
                                value={endUserTicketName}
                                placeholder="Tickets"
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setLastCustomTicketName(val);
                                  setLocalConfig(
                                    produce((draft) => {
                                      if (draft.auth?.supportTickets) {
                                        draft.auth.supportTickets.endUserTicketName =
                                          val;
                                      }
                                    })
                                  );
                                }}
                                variant="soft"
                                color="gray"
                              />
                            )}
                          </Flex>
                        </Flex>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            )}
          </Flex>
        </div>

        <Container className={styles.bottomSpacer} />
      </div>
    </ScrollArea>
  );
};
